// Definición del menú lateral del shell autenticado (Módulo Disciplinario).
// El RBAC de la UI se hace por ROL: cada ítem puede exigir una lista de `roles`;
// si el rol del usuario no está, no se muestra. Los ítems sin pantalla construida
// abren un Placeholder "en construcción".
import { ROLES_ABOGADOS_SUSTANCIADORES, ROLES_FILLER_ETAPA2 } from '@/lib/roles';

export interface MenuItem {
  id: string;
  label: string;
  icon: string;
  roles?: string[];   // si se define, solo estos roles ven el ítem
  submenu?: MenuItem[];
  // Si es true, el grupo NO es desplegable: el label se pinta como TÍTULO de
  // sección (con una línea blanca debajo) y sus opciones van como ítems
  // principales, al mismo nivel que "Inicio".
  seccion?: boolean;
}

// Roles con capacidad de administrar usuarios (directores + administrador +
// superusuario, cuyo ÚNICO trabajo es gestionar usuarios). El TÉCNICO
// ADMINISTRATIVO entra con gestión BÁSICA (pedido del cliente ago-06): solo
// crear y editar — ver ROLES_USUARIOS_GESTION_TOTAL.
export const ROLES_ADMIN_USUARIOS = [
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'SUPER_USUARIO',
  'ADMINISTRADOR',
  'TECNICO_ADMINISTRATIVO',
];

// Gestión TOTAL de usuarios (activar/inactivar, contraseñas, enlaces, firmas):
// los administradores de siempre, SIN el técnico (él solo crea y edita).
export const ROLES_USUARIOS_GESTION_TOTAL = [
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'SUPER_USUARIO',
  'ADMINISTRADOR',
];

// Roles que pueden ELIMINAR usuarios (decidido con el cliente): directores de
// instrucción y juzgamiento, superusuario y administrador.
export const ROLES_ELIMINAR_USUARIOS = [
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'SUPER_USUARIO',
  'ADMINISTRADOR',
];

// Roles que gestionan el reparto (solo el Director de Instrucción + administrador).
export const ROLES_REPARTO = ['DIRECTOR_INSTRUCCION', 'ADMINISTRADOR'];

// Roles que gestionan el reparto de juzgamiento (Director de Juzgamiento + administrador).
export const ROLES_REPARTO_JUZGAMIENTO = ['DIRECTOR_JUZGAMIENTO', 'ADMINISTRADOR'];

// Roles de segunda instancia (ven la bandeja de segunda instancia).
export const ROLES_SEGUNDA_INSTANCIA_UI = [
  'SEGUNDA_INSTANCIA',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'ADMINISTRADOR',
];

// Roles que ven los reportes gerenciales (MÓDULO 7): administrador, directores y
// supervisión (solo lectura).
export const ROLES_REPORTES_UI = [
  'ADMINISTRADOR',
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'SUPERVISOR',
  'APOYO_SUPERVISOR',
];

// Roles que gestionan notificaciones (MÓDULO 6): Secretaría, directores y admin.
export const ROLES_NOTIFICACIONES_UI = [
  'SECRETARIA',
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'ADMINISTRADOR',
];

// Roles de PARTE del portal ciudadano (con login): disciplinado y defensor.
// Estos roles solo ven "Mis actuaciones" (ningún ítem interno del módulo).
export const ROLES_PARTE_UI = ['DISCIPLINADO', 'DEFENSOR'];

// Roles que pueden INICIAR el flujo (radicar la queja). Coincide con el backend
// de expedientes: incluye TECNOLOGO_APOYO; NO incluye Abogado especializado.
export const ROLES_RADICAR_UI = [
  'DIRECTOR_INSTRUCCION',
  'FUNCIONARIO_INSTRUCCION',
  'CONTRATISTA_INSTRUCCION',
  'TECNOLOGO_APOYO',
  'SECRETARIA',
  'ADMINISTRADOR',
];

// Nuevo flujo: el Técnico administrativo y su apoyo (+ Admin) CREAN la pre-radicación (Etapa 1).
export const ROLES_CREADOR_PRERAD = [
  'TECNICO_ADMINISTRATIVO',
  'APOYO_TECNICO_ADMINISTRATIVO',
  'ADMINISTRADOR',
];
// Cadena del acta de reparto: el abogado sustanciador la revisa y el director la
// firma; el técnico también entra (ve sus devueltas y las firmadas por asignar).
export const ROLES_ACTAS_UI = [
  ...ROLES_ABOGADOS_SUSTANCIADORES,
  'DIRECTOR_INSTRUCCION',
  'TECNICO_ADMINISTRATIVO',
  'APOYO_TECNICO_ADMINISTRATIVO',
  'ADMINISTRADOR',
];
// «Control de ingreso» lo ven el creador (Etapa 1) y quienes diligencian la
// Etapa 2 (el abogado que recibió el caso en la asignación + el tecnólogo de
// apoyo, que ahí ven sus casos asignados).
export const ROLES_LIBRO_RADICADOR = [
  'TECNICO_ADMINISTRATIVO',
  'APOYO_TECNICO_ADMINISTRATIVO',
  'ADMINISTRADOR',
  ...ROLES_FILLER_ETAPA2,
];

// Calendario de comités de queja (ago-06, pedido del cliente): lo ven el técnico
// administrativo, su apoyo y el Director de Instrucción (+ admin).
export const ROLES_CALENDARIO_UI = [
  'TECNICO_ADMINISTRATIVO',
  'APOYO_TECNICO_ADMINISTRATIVO',
  'DIRECTOR_INSTRUCCION',
  'ADMINISTRADOR',
];

// Roles que ven la bandeja de decisión inicial (doble visto bueno): abogados
// revisores, Director de Instrucción, técnico, tecnólogo y supervisión (lectura).
export const ROLES_DECISION_UI = [
  ...ROLES_ABOGADOS_SUSTANCIADORES,
  'DIRECTOR_INSTRUCCION',
  'TECNICO_ADMINISTRATIVO',
  'APOYO_TECNICO_ADMINISTRATIVO',
  'TECNOLOGO_APOYO',
  'SUPERVISOR',
  'APOYO_SUPERVISOR',
  'ADMINISTRADOR',
];

// Roles de solo lectura (supervisión): ven expedientes/reportes, no modifican.
// El Apoyo al supervisor tiene exactamente los mismos permisos que el Supervisor.
export const ROLES_LECTURA_UI = ['SUPERVISOR', 'APOYO_SUPERVISOR'];

// Roles que ven expedientes (radicadores + abogado especializado + supervisión).
export const ROLES_INSTRUCCION_UI = [
  ...ROLES_RADICAR_UI,
  'ABOGADO_ESPECIALISTA', // consulta los expedientes donde esté añadido
  'FUNCIONARIO_JUZGAMIENTO',
  'CONTRATISTA_JUZGAMIENTO',
  ...ROLES_LECTURA_UI,
];

export const MENU: MenuItem[] = [
  { id: 'inicio', label: 'Inicio', icon: 'home' },
  { id: 'mis-actuaciones', label: 'Mis actuaciones', icon: 'list', roles: ROLES_PARTE_UI },
  {
    // Pedido del cliente (ago-06): "Usuarios" ya NO es desplegable; es un TÍTULO
    // de sección (con raya blanca) y sus opciones van como ítems principales,
    // igual que "Instrucción". La funcionalidad de cada vista no cambia.
    id: 'usuarios-grupo', label: 'Usuarios', icon: 'users', seccion: true,
    submenu: [
      { id: 'usuarios-crear', label: 'Crear usuario', icon: 'user-plus', roles: ROLES_ADMIN_USUARIOS },
      { id: 'usuarios', label: 'Ver usuarios', icon: 'users', roles: ROLES_ADMIN_USUARIOS },
      // Reporte del personal con contrato: plazos + alerta de vencimientos.
      { id: 'usuarios-reporte', label: 'Reporte', icon: 'chart', roles: ROLES_ADMIN_USUARIOS },
    ],
  },
  {
    // Pedido del cliente (ago-2026): "Instrucción" ya no es desplegable; es un
    // título de sección y sus opciones se muestran como ítems principales.
    id: 'instruccion', label: 'Instrucción', icon: 'folder', seccion: true,
    submenu: [
      { id: 'radicar-queja', label: 'Control de ingreso', icon: 'book', roles: ROLES_LIBRO_RADICADOR },
      // Agenda de los comités de queja programados en el Control de ingreso.
      { id: 'calendario', label: 'Calendario', icon: 'calendar', roles: ROLES_CALENDARIO_UI },
      // ELIMINADO del menú (pedido del cliente ago-05): el ítem "Actas de reparto"
      // (la antigua "Expedientes no iniciados") duplicaba el nombre del grupo de abajo.
      // Su listado (solicitudes EN_PROCESO por diligenciar) ahora vive en
      // «Actas de reparto → Ver» (chip "En diligenciamiento" + botón Continuar).
      // { id: 'expedientes-no-iniciados', label: 'Actas de reparto', icon: 'clock', roles: ROLES_CREADOR_PRERAD },
      // Cadena del acta de reparto (rediseño ago-2026): grupo con dos subopciones —
      // «Ver» (pendientes + filtros por estado) y «Reportes» (historial de todas).
      {
        id: 'actas-reparto', label: 'Actas de reparto', icon: 'pen', roles: ROLES_ACTAS_UI,
        submenu: [
          { id: 'actas-ver', label: 'Ver', icon: 'eye', roles: ROLES_ACTAS_UI },
          { id: 'actas-reportes', label: 'Reportes', icon: 'chart', roles: ROLES_ACTAS_UI },
        ],
      },
      // El id de vista sigue siendo `casos-aprobados` (identificador técnico);
      // solo cambia la etiqueta visible (pedido del cliente ago-06).
      { id: 'casos-aprobados', label: 'Expedientes aprobados', icon: 'badge-check', roles: ROLES_CREADOR_PRERAD },
      { id: 'decision-inicial', label: 'Decisión inicial', icon: 'gavel', roles: ROLES_DECISION_UI },
      { id: 'mis-expedientes', label: 'Mis expedientes', icon: 'folder', roles: ROLES_INSTRUCCION_UI },
      // OCULTO (pedido del cliente ago-2026): "Actuaciones" no se necesita por
      // ahora. Descomentar para reactivarla.
      // { id: 'instruccion-actuaciones', label: 'Actuaciones', icon: 'pencil' },
    ],
  },
  {
    // Título de sección "Mi perfil": agrupa lo personal del usuario en sesión.
    // Cada usuario carga SU PROPIA firma (misma vista del avatar → «Mi firma»).
    id: 'mi-perfil', label: 'Mi perfil', icon: 'pen', seccion: true,
    submenu: [
      { id: 'mi-firma', label: 'Cargar firma', icon: 'pen' },
    ],
  },
  // OCULTO (pedido del cliente ago-2026): la sección Juzgamiento no se muestra
  // para ningún usuario por ahora. Descomentar para reactivarla.
  // {
  //   id: 'juzgamiento', label: 'Juzgamiento', icon: 'gavel',
  //   submenu: [
  //     { id: 'juzgamiento-bandeja', label: 'Reparto Juzgamiento', icon: 'inbox', roles: ROLES_REPARTO_JUZGAMIENTO },
  //     { id: 'juzgamiento-audiencias', label: 'Audiencias', icon: 'scale' },
  //     { id: 'juzgamiento-fallos', label: 'Fallos', icon: 'gavel' },
  //   ],
  // },
  {
    id: 'segunda', label: 'Segunda instancia', icon: 'scale',
    submenu: [
      { id: 'segunda-bandeja', label: 'Segunda instancia', icon: 'scale', roles: ROLES_SEGUNDA_INSTANCIA_UI },
    ],
  },
  // OCULTO (pedido del cliente ago-2026): las secciones Reparto, Notificaciones
  // y Reportes no se muestran por ahora. Descomentar para reactivarlas.
  // {
  //   id: 'reparto', label: 'Reparto', icon: 'share',
  //   submenu: [
  //     { id: 'reparto-bandeja', label: 'Bandeja', icon: 'inbox', roles: ROLES_REPARTO },
  //     { id: 'reparto-carga', label: 'Carga laboral', icon: 'chart', roles: ROLES_REPARTO },
  //   ],
  // },
  // {
  //   id: 'notificaciones', label: 'Notificaciones', icon: 'bell',
  //   submenu: [
  //     { id: 'notificaciones-central', label: 'Central de notificaciones', icon: 'bell', roles: ROLES_NOTIFICACIONES_UI },
  //   ],
  // },
  // OCULTO (pedido del cliente ago-2026): la sección Términos no se muestra
  // para ningún usuario por ahora. Descomentar para reactivarla.
  // {
  //   id: 'terminos', label: 'Términos', icon: 'calendar',
  //   submenu: [
  //     { id: 'terminos-cronograma', label: 'Cronograma', icon: 'calendar' },
  //   ],
  // },
  // {
  //   id: 'reportes', label: 'Reportes', icon: 'chart',
  //   submenu: [
  //     { id: 'reportes', label: 'Reportes gerenciales', icon: 'chart', roles: ROLES_REPORTES_UI },
  //     // Plazos de contratos: información del cliente dirigida al Director de
  //     // Instrucción (avisos con 1 mes / 15 días / 1 semana de anticipación).
  //     { id: 'reportes-plazos', label: 'Plazos', icon: 'clock', roles: ['DIRECTOR_INSTRUCCION', 'ADMINISTRADOR'] },
  //   ],
  // },
];

/** ¿El usuario (por su rol) puede ver este ítem? */
export function puedeVer(item: MenuItem, rol: string): boolean {
  return !item.roles || item.roles.includes(rol);
}

/** Menú filtrado por rol: oculta ítems/submenús que el usuario no puede ver. */
export function menuVisible(rol: string): MenuItem[] {
  // Las partes del portal (disciplinado/defensor) solo ven "Mis actuaciones";
  // no se les muestra ningún ítem interno del módulo (ni "Inicio").
  if (ROLES_PARTE_UI.includes(rol)) {
    return MENU.filter((m) => m.id === 'mis-actuaciones');
  }
  // El superusuario SOLO gestiona usuarios: Crear/Ver usuarios y nada más
  // (sin Inicio ni el resto del módulo; decidido con el cliente).
  if (rol === 'SUPER_USUARIO') {
    return MENU.filter((m) => m.id === 'usuarios-grupo');
  }
  return MENU.map((m) => {
    if (!m.submenu) return puedeVer(m, rol) ? m : null;
    const subs = m.submenu
      .filter((s) => puedeVer(s, rol))
      .map((s) => etiquetaPorRol(s, rol))
      // Sub-submenús (p. ej. Actas de reparto → Ver/Reportes): filtrar sus hijos
      // por rol y ocultar el grupo si queda vacío.
      .map((s) => (s.submenu ? { ...s, submenu: s.submenu.filter((x) => puedeVer(x, rol)) } : s))
      .filter((s) => !s.submenu || s.submenu.length > 0);
    return subs.length ? { ...m, submenu: subs } : null;
  }).filter((m): m is MenuItem => m !== null);
}

// Renombra ítems del menú según el rol. El Técnico administrativo NO decide en
// "Decisión inicial": su bandeja ahí solo recibe los casos DEVUELTOS para corregir la
// pre-radicación (Etapa 1), así que se le muestra como "Casos devueltos".
function etiquetaPorRol(item: MenuItem, rol: string): MenuItem {
  if (
    item.id === 'decision-inicial'
    && (rol === 'TECNICO_ADMINISTRATIVO' || rol === 'APOYO_TECNICO_ADMINISTRATIVO')
  ) {
    return { ...item, label: 'Expedientes devueltos' };
  }
  return item;
}
